import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, MessageCircle } from "lucide-react";

export function ChatSheet({ children, startWithProfessionalId, onActivity }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const pollRef = useRef(null);
    const bottomRef = useRef(null);

    const loadConversations = useCallback(async () => {
        const { data } = await api.get("/conversations");
        setConversations(data);
        return data;
    }, []);

    const openConversation = useCallback(async (conv) => {
        setActive(conv);
        const { data } = await api.get(`/conversations/${conv.id}/messages`);
        setMessages(data);
        onActivity?.();
    }, [onActivity]);

    // when sheet opens
    useEffect(() => {
        if (!open) return;
        (async () => {
            const convs = await loadConversations();
            if (startWithProfessionalId) {
                try {
                    const { data } = await api.post("/conversations", { professional_id: startWithProfessionalId });
                    await openConversation(data);
                    loadConversations();
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            } else if (convs.length && !active) {
                // stay on list
            }
        })();
        // eslint-disable-next-line
    }, [open]);

    // poll active thread
    useEffect(() => {
        if (!open || !active) return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/conversations/${active.id}/messages`);
                setMessages(data);
                onActivity?.();
            } catch {}
        }, 20000);
        return () => clearInterval(pollRef.current);
    }, [open, active]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // realtime: refresh active thread + list on incoming ws events
    useEffect(() => {
        if (!open) return;
        const onWs = async (e) => {
            const d = e.detail || {};
            if (active && d.conversation_id === active.id) {
                try {
                    const { data } = await api.get(`/conversations/${active.id}/messages`);
                    setMessages(data);
                    onActivity?.();
                } catch {}
            }
            loadConversations();
        };
        window.addEventListener("fipro:ws", onWs);
        return () => window.removeEventListener("fipro:ws", onWs);
    }, [open, active, loadConversations, onActivity]);

    const send = async () => {
        if (!text.trim() || !active) return;
        const t = text;
        setText("");
        try {
            const { data } = await api.post(`/conversations/${active.id}/messages`, { text: t });
            setMessages((m) => [...m, data]);
            loadConversations();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    if (!user) return null;

    return (
        <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setActive(null); setMessages([]); onActivity?.(); } }}>
            <SheetTrigger asChild>{children}</SheetTrigger>
            <SheetContent className="bg-white w-full sm:max-w-md p-0 flex flex-col" data-testid="chat-sheet">
                <SheetHeader className="px-5 py-4 border-b border-border">
                    <SheetTitle className="font-heading flex items-center gap-2">
                        {active ? (
                            <button onClick={() => { setActive(null); setMessages([]); }} className="flex items-center gap-2" data-testid="chat-back">
                                <ArrowLeft className="h-4 w-4" /> {active.counterpart_name}
                            </button>
                        ) : (
                            <span className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-terracotta" /> Messaggi</span>
                        )}
                    </SheetTitle>
                </SheetHeader>

                {!active ? (
                    <div className="flex-1 overflow-y-auto">
                        {conversations.length === 0 ? (
                            <p className="p-6 text-center text-ink/50 text-sm">Nessuna conversazione ancora.</p>
                        ) : conversations.map((c) => (
                            <button key={c.id} onClick={() => openConversation(c)}
                                className="w-full flex items-center gap-3 px-5 py-4 border-b border-border hover:bg-creamalt text-left transition-colors"
                                data-testid={`conv-${c.id}`}>
                                <div className="h-11 w-11 rounded-xl bg-ink text-white flex items-center justify-center font-semibold overflow-hidden shrink-0">
                                    {c.counterpart_photo ? <img src={c.counterpart_photo} alt="" className="h-full w-full object-cover" /> : c.counterpart_name?.[0]}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-ink truncate">{c.counterpart_name}</p>
                                    <p className="text-sm text-ink/50 truncate">{c.last_message || "Avvia la conversazione"}</p>
                                </div>
                                {c.unread_count > 0 && (
                                    <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-terracotta text-white text-[11px] font-bold flex items-center justify-center shrink-0" data-testid={`conv-unread-${c.id}`}>
                                        {c.unread_count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-creamalt/40">
                            {messages.length === 0 && <p className="text-center text-ink/40 text-sm mt-8">Scrivi il primo messaggio 👋</p>}
                            {messages.map((m) => (
                                <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.mine ? "bg-ink text-white rounded-br-sm" : "bg-white border border-border text-ink rounded-bl-sm"}`}>
                                        {m.text}
                                    </div>
                                </div>
                            ))}
                            <div ref={bottomRef} />
                        </div>
                        <div className="p-3 border-t border-border flex items-center gap-2">
                            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                                placeholder="Scrivi un messaggio…" className="bg-white" data-testid="chat-input" />
                            <Button onClick={send} size="icon" className="rounded-full bg-terracotta hover:bg-terracottahover shrink-0" data-testid="chat-send">
                                <Send className="h-4 w-4" />
                            </Button>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
